// Flow configuration system to support multiple user journeys
// This allows us to share components while having different flows

export const FLOW_TYPES = {
  INSURANCE_PROFILE: "insurance-profile",
  EXTENDED_WARRANTY: "extended-warranty",
  NATIONWIDE: "nationwide",
};

// Insurance Profile Flow Configuration (existing)
const INSURANCE_PROFILE_CONFIG = {
  flowType: FLOW_TYPES.INSURANCE_PROFILE,
  baseRoute: "",
  defaultRoute: "/welcome",

  steps: {
    WELCOME: "welcome",
    CONTACT_INFO: "contact-info",
    ALARM_VERIFY: "alarm/verify",
    SURVEY: "survey",
    VERIFICATION: "verification",
    CONFIRMATION_STATUS: "confirmation-status",
    REWARD: "reward",
  },

  // Define the step flow logic
  // stepFlow: {
  //   welcome: ({ isReturningUser, user, enableDimo }) => {
  //     if (isReturningUser && user) {
  //       return enableDimo ? 'dimo' : 'survey';
  //     }
  //     return 'verification';
  //   },
  //   verification: ({ enableDimo }) => enableDimo ? 'dimo' : 'survey',
  //   dimo: () => 'survey',
  //   survey: () => 'contact-info',
  //   'contact-info': () => 'post-auth-survey',
  //   'post-auth-survey': () => 'insurance-options',
  //   'insurance-options': () => 'success', // Can be overridden by component
  //   document: () => 'insurance-options',
  //   canopy: () => 'success',
  //   'review-submit': () => 'success',
  //   success: () => 'success',
  // },

  // Survey configuration
  surveys: {
    initial: "STEM_FwXtLCa2fL",
    postAuth: "STEM_WPFZm8Kfn1",
    main: "STEM_8Dl2h4lryX",
  },

  // UI configuration
  ui: {
    showProgressIndicator: true,
    progressExcludeSteps: [
      "welcome",
      "confirmation-status",
      "review-submit",
      "verification",
      "dimo",
      "success",
    ],
    welcomeBackground: "bg-gray-50",
    partnerNameKey: "partnerName", // Key for partner name in display config
  },

  // Feature flags
  features: {
    enableDimo: false, // Can be overridden by referrer logic
    enableCanopy: false,
    enableDocumentUpload: false,
  },
};

// Extended Warranty Flow Configuration (new)
const EXTENDED_WARRANTY_CONFIG = {
  flowType: FLOW_TYPES.EXTENDED_WARRANTY,
  baseRoute: "/extended-warranty",
  defaultRoute: "/extended-warranty/welcome",

  steps: {
    WELCOME: "welcome",
    VERIFICATION: "verification",
    SURVEY: "survey", // Extended warranty specific survey
    CONTACT_INFO: "contact-info",
    REWARD: "reward",
  },

  // Define the step flow logic for extended warranty
  stepFlow: {
    welcome: () => "qualification",
    qualification: ({ qualifies }) => (qualifies ? "verification" : "exit"),
    verification: () => "survey",
    survey: ({ surveyAnswers }) => {
      // Analyze survey answers to determine if user is interested
      const isInterested = isInterestedInWarranty(surveyAnswers || {});
      return isInterested ? "extended-warranty-choice" : "exit"; // NEW: Go to choice screen instead of vehicle-check
    },
    "extended-warranty-choice": ({ choice }) => {
      // NEW: Route based on user's verification choice
      if (choice === "policy") {
        return "extended-warranty-policy";
      } else if (choice === "mileage") {
        return "extended-warranty-mileage";
      }
      return "extended-warranty-choice"; // Stay on choice screen if no valid choice
    },
    "extended-warranty-policy": () => "success", // NEW: Go to success after policy upload
    "extended-warranty-mileage": () => "success", // NEW: Go to success after mileage check
    "vehicle-check": ({ hasExistingVehicles }) =>
      hasExistingVehicles ? "vehicle-selection" : "document",
    "vehicle-selection": () => "vehicle-completion", // TEST: Go to completion
    "vehicle-completion": () => "handoff-test", // TEST: Go to handoff test
    "extended-warranty-offer": ({ accepted }) =>
      accepted ? "success" : "exit", // NEW
    "handoff-test": () => "handoff-test", // TEST: Terminal state
    document: ({ vinExtracted }) =>
      vinExtracted ? "handoff" : "photo-capture",
    "photo-capture": () => "handoff",
    handoff: () => "handoff", // Terminal state
    success: () => "success", // NEW: Terminal state
    exit: () => "exit", // Terminal state
  },

  // Survey configuration
  surveys: {
    main: "STEM_8Dl2h4lryX", // Extended Warranty survey
  },

  // UI configuration
  ui: {
    showProgressIndicator: true,
    progressExcludeSteps: [
      "welcome",
      "qualification",
      "verification",
      "extended-warranty-choice",
      "extended-warranty-offer",
      "success",
      "extended-warranty-handoff",
      "handoff",
      "handoff-test",
    ], // NEW: Exclude choice and success screens
    welcomeBackground: "bg-blue-50", // Different styling
    partnerNameKey: "warrantyPartnerName",
  },

  // Feature flags
  features: {
    enableDimo: true,
    enablePolicyUpload: true,
    enablePhotoCapture: true,
    enableBlackboxAPI: true,
  },

  // Extended warranty specific config
  warranty: {
    minCarAge: 4, // Minimum car age in years
    chaizBaseUrl: "https://www.chaiz.com",
    blackboxApiEndpoint: "/api/users/vehicles/all", // TBD - endpoint for vehicle data
  },
};

// Nationwide Flow Configuration (new)
const NATIONWIDE_CONFIG = {
  flowType: FLOW_TYPES.NATIONWIDE,
  baseRoute: "",
  defaultRoute: "/welcome",

  steps: {
    WELCOME: "welcome",
    VERIFICATION: "verification",
    ALARM_VERIFY: "alarm/verify",
    SURVEY: "survey",
    DOCUMENT_UPLOAD: "document-upload",
    CONTACT_INFO: "contact-info",
    REWARD: "reward",
    SUCCESS: "success",
    THANK_YOU: "thank-you",
  },

  // Define the step flow logic for Nationwide
  stepFlow: {
    welcome: ({ isReturningUser, user }) => {
      if (isReturningUser && user) {
        return "survey";
      }
      return "verification";
    },
    verification: () => "survey",
    survey: ({ requiresDocumentUpload }) =>
      requiresDocumentUpload ? "document-upload" : "thank-you",
    "document-upload": () => "contact-info",
    "contact-info": () => "success",
    success: () => "success", // Terminal state - can navigate to reward from here
    reward: () => "reward", // Terminal state
    "thank-you": () => "thank-you", // Terminal state
  },

  // Survey configuration
  surveys: {
    main: "STEM_nationwide", // TODO: Update with actual Nationwide survey ID
  },

  // UI configuration
  ui: {
    showProgressIndicator: true,
    progressExcludeSteps: ["welcome", "verification", "success"],
    welcomeBackground: "bg-blue-50", // Nationwide blue branding
    partnerNameKey: "partnerName",
  },

  // Feature flags
  features: {
    enableDimo: false,
    enableCanopy: false,
    enableDocumentUpload: true, // Enable document upload for Nationwide
    enableSurvey: true,
  },
};

// Export the configurations
export const FLOW_CONFIGS = {
  [FLOW_TYPES.INSURANCE_PROFILE]: INSURANCE_PROFILE_CONFIG,
  [FLOW_TYPES.EXTENDED_WARRANTY]: EXTENDED_WARRANTY_CONFIG,
  [FLOW_TYPES.NATIONWIDE]: NATIONWIDE_CONFIG,
};

// Helper functions
export const getFlowConfig = (flowType) => {
  return FLOW_CONFIGS[flowType] || FLOW_CONFIGS[FLOW_TYPES.INSURANCE_PROFILE];
};

export const getStepRoute = (flowType, step) => {
  const config = getFlowConfig(flowType);
  return `${config.baseRoute}/${step}`;
};

export const getNextStep = (flowType, currentStep, context = {}) => {
  const config = getFlowConfig(flowType);
  const stepFlowFn = config.stepFlow[currentStep];

  if (!stepFlowFn) {
    console.warn(`No step flow defined for ${currentStep} in ${flowType}`);
    return null;
  }

  if (typeof stepFlowFn === "function") {
    return stepFlowFn(context);
  }

  return stepFlowFn; // If it's just a string
};

// Flow detection helper
export const detectFlowType = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // Check subdomain first
  if (hostname.includes("nationwide")) {
    return FLOW_TYPES.NATIONWIDE;
  }

  if (hostname.includes("extended-warranty")) {
    return FLOW_TYPES.EXTENDED_WARRANTY;
  }

  // Check path
  if (pathname.startsWith("/nationwide")) {
    return FLOW_TYPES.NATIONWIDE;
  }

  if (pathname.startsWith("/extended-warranty")) {
    return FLOW_TYPES.EXTENDED_WARRANTY;
  }

  // Default to insurance profile
  return FLOW_TYPES.INSURANCE_PROFILE;
};

// Extended Warranty Survey Analysis
// Analyzes survey answers to determine if user is interested in warranty
const isInterestedInWarranty = (surveyAnswers) => {
  if (!surveyAnswers || Object.keys(surveyAnswers).length === 0) {
    return false;
  }

  // Check for positive indicators based on survey structure
  // Looking for answers that indicate interest in learning more or getting offers

  // Question: "Would you like to learn more about how an extended warranty could protect you?"
  // (SQUE_sGtHVhtY9y)
  const learnMoreAnswer = surveyAnswers["SQUE_sGtHVhtY9y"]?.value;
  if (learnMoreAnswer === "Yes") {
    return true;
  }

  // Question: "Want to learn more?" (for those who can't pay $1000+ repair)
  // (SQUE_yQOvhM7X1L)
  const finalLearnMoreAnswer = surveyAnswers["SQUE_yQOvhM7X1L"]?.value;
  if (finalLearnMoreAnswer === "Yes" || finalLearnMoreAnswer === "not_sure") {
    return true;
  }

  // If user has existing warranty and learned about switching/comparing
  // (SQUE_quVcqTR6oz === 'Yes' and reached instruction screens)
  const hasWarranty = surveyAnswers["SQUE_quVcqTR6oz"]?.value;
  if (hasWarranty === "Yes") {
    // If they have warranty and completed the flow, they're potentially interested in comparing
    const completedFlow = Object.keys(surveyAnswers).length >= 3;
    return completedFlow;
  }

  // Default to not interested if we can't determine clear interest
  return false;
};
